import { useEffect, useMemo, useState } from "react";
import {
  countDynamicContentRules,
  formatBadge,
  VARIANT_RULE_TYPES,
  type ComponentVariant,
  type VariantRuleType,
} from "@ease/page-builder/dynamic-content";
import { useStylePanel } from "./style-panel-context";

export type DynamicPreviewPreset =
  | "default"
  | "paid-mobile"
  | "france-organic"
  | "known-returning";

const PRESET_OPTIONS: Array<{ id: DynamicPreviewPreset; label: string }> = [
  { id: "default", label: "Default visitor" },
  { id: "paid-mobile", label: "Paid mobile visitor" },
  { id: "france-organic", label: "French organic visitor" },
  { id: "known-returning", label: "Known returning contact" },
];

function createVariant(index: number): ComponentVariant {
  const ruleType: VariantRuleType = "utm_campaign";
  const ruleValue = `audience_${index + 1}`;

  return {
    id: `variant-${Date.now().toString(36)}-${index + 1}`,
    label: `Audience ${index + 1}`,
    rule: {
      type: ruleType,
      value: ruleValue,
      operator: "equals",
    },
    badge: formatBadge({ type: ruleType, value: ruleValue }),
    overrides: {},
    priority: index + 1,
  };
}

export function DynamicContentPanel({
  previewPreset,
  onPreviewPresetChange,
}: {
  previewPreset: DynamicPreviewPreset;
  onPreviewPresetChange: (preset: DynamicPreviewPreset) => void;
}) {
  const {
    data,
    selectedComponentId,
    selectedComponentType,
    getSelectedComponentProps,
    setSelectedComponentProps,
  } = useStylePanel();

  const selectedProps = getSelectedComponentProps();
  const variants = useMemo(() => {
    const raw = selectedProps?.variants;
    return Array.isArray(raw) ? raw as ComponentVariant[] : [];
  }, [selectedProps]);
  const [overrideDrafts, setOverrideDrafts] = useState<Record<string, string>>({});

  useEffect(() => {
    setOverrideDrafts(
      Object.fromEntries(
        variants.map((variant) => [variant.id, JSON.stringify(variant.overrides, null, 2)]),
      ),
    );
  }, [variants]);

  const totalRuleCount = useMemo(
    () => countDynamicContentRules(data),
    [data],
  );

  const updateVariants = (nextVariants: ComponentVariant[]) => {
    if (!selectedProps) return;
    const nextProps = { ...selectedProps };
    if (nextVariants.length === 0) {
      delete nextProps.variants;
    } else {
      nextProps.variants = nextVariants;
    }
    setSelectedComponentProps(nextProps);
  };

  const updateVariant = (variantId: string, patch: Partial<ComponentVariant>) => {
    updateVariants(
      variants.map((variant) => {
        if (variant.id !== variantId) return variant;
        const nextRule = patch.rule ? patch.rule : variant.rule;
        const nextVariant = {
          ...variant,
          ...patch,
          rule: nextRule,
        };
        return {
          ...nextVariant,
          badge: formatBadge(nextRule),
        };
      }),
    );
  };

  if (!selectedComponentId || !selectedProps) {
    return (
      <div className="space-y-3 p-4">
        <h2 className="text-sm font-semibold text-foreground">Dynamic Content</h2>
        <p className="text-sm leading-6 text-muted-foreground">
          Select a block first. Dynamic variants are authored per component and saved inside the page document.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Dynamic Content</p>
        <h2 className="text-base font-semibold text-foreground">
          {selectedComponentType ?? "component"} [{selectedComponentId}]
        </h2>
        <p className="text-sm leading-6 text-muted-foreground">
          Variants are stored on the selected block and resolved with the same runtime rules used by published pages.
        </p>
      </div>

      <div className="rounded-2xl border border-border bg-background p-3">
        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Preview as
        </label>
        <select
          value={previewPreset}
          onChange={(event) => onPreviewPresetChange(event.target.value as DynamicPreviewPreset)}
          className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground outline-none"
        >
          {PRESET_OPTIONS.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>
      </div>

      <div className="rounded-2xl border border-border bg-background p-3 text-sm text-muted-foreground">
        <div className="flex items-center justify-between gap-3">
          <span>Total variant rules on this page</span>
          <span className="font-medium text-foreground">{totalRuleCount}/20</span>
        </div>
      </div>

      {variants.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-background px-4 py-5 text-sm leading-6 text-muted-foreground">
          No variants on this block yet. Add one to tailor copy or props for a specific audience.
        </div>
      ) : (
        <div className="space-y-3">
          {variants.map((variant) => (
            <article key={variant.id} className="rounded-2xl border border-border bg-background p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between gap-3">
                <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                  {variant.badge}
                </span>
                <button
                  type="button"
                  onClick={() => updateVariants(variants.filter((entry) => entry.id !== variant.id))}
                  className="text-xs font-medium text-destructive"
                >
                  Remove
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Label</label>
                  <input
                    value={variant.label}
                    onChange={(event) => updateVariant(variant.id, { label: event.target.value })}
                    className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground outline-none"
                  />
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Rule Type</label>
                    <select
                      value={variant.rule.type}
                      onChange={(event) => {
                        const nextType = event.target.value as VariantRuleType;
                        updateVariant(variant.id, {
                          rule: {
                            ...variant.rule,
                            type: nextType,
                          },
                        });
                      }}
                      className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground outline-none"
                    >
                      {VARIANT_RULE_TYPES.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Match Value</label>
                    <input
                      value={variant.rule.value}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        updateVariant(variant.id, {
                          rule: {
                            ...variant.rule,
                            value: nextValue,
                          },
                        });
                      }}
                      className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Priority</label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={variant.priority}
                    onChange={(event) => updateVariant(variant.id, { priority: Number(event.target.value) || 1 })}
                    className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground outline-none"
                  />
                </div>

              <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    Overrides (JSON object)
                  </label>
                  <textarea
                    value={overrideDrafts[variant.id] ?? JSON.stringify(variant.overrides, null, 2)}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setOverrideDrafts((current) => ({ ...current, [variant.id]: nextValue }));
                      try {
                        const parsed = JSON.parse(nextValue) as Record<string, unknown>;
                        updateVariant(variant.id, { overrides: parsed });
                      } catch {
                        // Keep invalid JSON local to the textarea until the user fixes it.
                      }
                    }}
                    rows={5}
                    className="w-full rounded-xl border border-border bg-card px-3 py-2 font-mono text-xs text-foreground outline-none"
                  />
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">
                    Only include the props you want to override for this audience. The runtime merges these onto the block’s default props.
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <button
        type="button"
        disabled={totalRuleCount >= 20}
        onClick={() => updateVariants([...variants, createVariant(variants.length)])}
        className="w-full rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
      >
        {totalRuleCount >= 20 ? "20 dynamic rules reached for this page" : "Add dynamic variant"}
      </button>
    </div>
  );
}
